import React, { useState } from 'react';
import { Search, User, Mail, Phone, MapPin, AlertTriangle, Loader2 } from 'lucide-react';

interface SearchState {
  loading: boolean;
  results: Record<string, unknown>[] | null;
  error: string | null;
}

export const SearchPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'email' | 'phone' | 'name'>('email');
  const [searchState, setSearchState] = useState<SearchState>({
    loading: false,
    results: null,
    error: null
  });

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setSearchState({ loading: false, results: null, error: 'Please enter a search term' });
      return;
    }

    setSearchState({ loading: true, results: null, error: null });

    // Simulate API call delay
    setTimeout(() => {
      // Since no real API is connected, show "No data available" message
      setSearchState({
        loading: false,
        results: null,
        error: 'No search API connected. Connect to backend API to enable individual search functionality.'
      });
    }, 1500);
  };

  const EmptyState: React.FC<{ message: string }> = ({ message }) => (
    <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
      <AlertTriangle className="h-8 w-8 text-slate-400 mx-auto mb-3" />
      <p className="text-slate-600">{message}</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Individual Search</h1>
        <p className="text-slate-600 mt-1">Search for individual contacts and view detailed information</p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Search Term
              </label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter email, phone, or name to search..."
                  className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={searchState.loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Search Type
              </label>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as 'email' | 'phone' | 'name')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={searchState.loading}
              >
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="name">Name</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="submit"
                disabled={searchState.loading || !searchQuery.trim()}
                className="w-full ca-btn ca-btn--primary font-medium disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {searchState.loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Results Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-medium text-slate-900">Search Results</h2>

        {searchState.loading && (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-slate-600 mx-auto mb-4" />
            <p className="text-slate-600">Searching for "{searchQuery}"...</p>
          </div>
        )}

        {searchState.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-red-900">Search Error</h3>
                <p className="text-red-700 mt-1">{searchState.error}</p>
              </div>
            </div>
          </div>
        )}

        {searchState.results === null && !searchState.loading && !searchState.error && (
          <EmptyState message="Enter a search term above to find individual contact information." />
        )}

        {searchState.results && searchState.results.length === 0 && (
          <EmptyState message="No results found for your search query." />
        )}

        {/* Results would be displayed here when real API is connected */}
        {searchState.results && searchState.results.length > 0 && (
          <div className="space-y-4">
            {/* Individual result cards would be rendered here */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-slate-500" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-medium text-slate-900">Contact Name</h3>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Verified
                    </span>
                  </div>
                  <div className="space-y-1 text-sm text-slate-600">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2" />
                      <span>email@example.com</span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2" />
                      <span>(555) 123-4567</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span>City, State 12345</span>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <div className="text-right">
                    <div className="text-lg font-semibold text-slate-900">85%</div>
                    <div className="text-xs text-slate-500">CollectABILITY Score</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

